import classnames from "classnames";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { Card, Col, Container, Nav, NavItem, NavLink, Row } from "reactstrap";

import { adminStatsEndpoints, subscriptionTiersEndpoints, type User } from "../../../../api";
import KpiTile from "../../../../components/admin/KpiTile";
import PageHeader from "../../../../components/admin/PageHeader";
import usePagination from "../../../../components/hooks/usePagination";
import MapGraph, { type MapDataItem } from "../../../../components/stats/MapGraph";
import useUsers from "../../../../components/users/hooks/useUsers";
import UsersListCard from "../../../../components/users/UsersListCard";
import useFetch from "../../../../hooks/useFetch";
import { isZeroPriceTier } from "../../../../utils/isZeroPriceTier";

type Segment = "clients" | "nonClients" | "vip";
const SEGMENTS: Segment[] = ["clients", "nonClients", "vip"];

const hasRunningBot = (user: User) =>
  !!user.exchange_accounts?.some((account) => account.bot_configs?.some((bot) => !bot.disabled));

/**
 * Single users page with segment tabs (replaces Clients / NonClientUsers / VIPUsers pages).
 */
const Users: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "adminPages.users" });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const segmentParam = searchParams.get("segment");
  const segment: Segment = SEGMENTS.includes(segmentParam as Segment) ? (segmentParam as Segment) : "clients";
  const setSegment = (s: Segment) => setSearchParams({ segment: s }, { replace: true });

  const { users: clients = [], isLoading: loadingClients, ...clientsProps } = useUsers({
    sort: { createdAt: "desc" },
    filters: { role: { name: "Authenticated" }, bot_configs: { id: { $notNull: true } } },
  });
  const { users: nonClients = [], isLoading: loadingNonClients, ...nonClientsProps } = useUsers({
    sort: { createdAt: "desc" },
    filters: { role: { name: "Authenticated" }, bot_configs: { id: { $null: true } } },
  });
  // The VIP segment must include everyone on a free tier, not just the legacy
  // isVIP flag, which is being deprecated. "Free" is decided by the same
  // zero-price rule the rest of the app gates behaviour on, so the tier list
  // is resolved first and matched by id.
  const { state: allTiers } = useFetch(() => subscriptionTiersEndpoints.getAll().then(({ data }) => data ?? []), {
    cacheKey: "subscription_tiers_all",
  });
  const zeroPriceTierIds = useMemo(
    () => (allTiers ?? []).filter(isZeroPriceTier).map(({ id }) => id),
    [allTiers]
  );
  const { users: vip = [], isLoading: loadingVip, ...vipProps } = useUsers({
    sort: { createdAt: "down" },
    filters: zeroPriceTierIds.length
      ? { $or: [{ isVIP: true }, { subscription_tier: { id: { $in: zeroPriceTierIds } } }] }
      : { isVIP: true },
  });
  const { users: unverified = [] } = useUsers({ filters: { role: { name: "Not verified" } } });

  const clientsWithActiveBot = useMemo(() => clients.filter(hasRunningBot).length, [clients]);
  const vipSorted = useMemo(
    () => [...vip].sort((a, b) => (hasRunningBot(b) ? 1 : 0) - (hasRunningBot(a) ? 1 : 0)),
    [vip]
  );

  const { state: perCountryResponse, isLoading: loadingMap } = useFetch(
    () => adminStatsEndpoints.getUsersPerCountry().then(({ data }) => data),
    { cacheKey: "users_per_country" }
  );
  const perCountry = perCountryResponse?.perCountry ?? {};

  const current = {
    clients: { users: clients, isLoading: loadingClients, props: clientsProps, hide: [] as string[] },
    nonClients: {
      users: nonClients,
      isLoading: loadingNonClients,
      props: nonClientsProps,
      hide: ["bots", "lastBotUpdate", "referrals"],
    },
    vip: { users: vipSorted, isLoading: loadingVip, props: vipProps, hide: [] as string[] },
  }[segment];

  // The list is paginated client-side over the full result set, so sorting is
  // done here rather than round-tripping to the server.
  const [userSort, setUserSort] = useState<Record<string, string | null>>({});
  const sortedUsers = useMemo(() => {
    const [field, direction] = Object.entries(userSort).find(([, d]) => !!d) ?? [];
    if (!field || !direction) return current.users;

    const valueOf = (user: User): string | number => {
      switch (field) {
        case "numBots":
          return (user.exchange_accounts ?? []).reduce((n, ea) => n + (ea.bot_configs?.length ?? 0), 0);
        case "numReferrals":
          return user.referrals?.length ?? 0;
        case "tier":
          return user.subscription_tier?.name ?? "";
        case "name":
        default:
          return (user.name || user.email || "").toLowerCase();
      }
    };

    const factor = direction === "up" || direction === "asc" ? 1 : -1;
    return [...current.users].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * factor;
      return String(av).localeCompare(String(bv)) * factor;
    });
  }, [current.users, userSort]);

  const { pageItems: pageUsers, ...paginationProps } = usePagination(sortedUsers, { pageSize: 10 });

  const tabs: { key: Segment; label: string; count: number; icon: string }[] = [
    { key: "clients", label: t("segments.clients"), count: clients.length, icon: "fas fa-user-check" },
    { key: "nonClients", label: t("segments.nonClients"), count: nonClients.length, icon: "fas fa-user-clock" },
    { key: "vip", label: t("segments.vip"), count: vip.length, icon: "fas fa-crown" },
  ];

  return (
    <Container className="mt-4" fluid>
      <PageHeader title={t("title")} subtitle={t("subtitle")} />

      <Row className="mb-3" style={{ rowGap: 12 }}>
        <Col xs={12} sm={6} xl={3}>
          <KpiTile
            label={t("kpi.clients")}
            value={clients.length}
            detail={t("kpi.clientsDetail", { active: clientsWithActiveBot })}
            icon="fas fa-users"
            isLoading={loadingClients}
          />
        </Col>
        <Col xs={12} sm={6} xl={3}>
          <KpiTile
            label={t("kpi.nonClients")}
            value={nonClients.length}
            detail={t("kpi.nonClientsDetail")}
            icon="fas fa-user-clock"
            isLoading={loadingNonClients}
          />
        </Col>
        <Col xs={12} sm={6} xl={3}>
          <KpiTile
            label={t("kpi.vip")}
            value={vip.length}
            detail={t("kpi.vipDetail", { active: vipSorted.filter(hasRunningBot).length })}
            icon="fas fa-crown"
            tone="accent"
            isLoading={loadingVip}
          />
        </Col>
        <Col xs={12} sm={6} xl={3}>
          <KpiTile
            label={t("kpi.unverified")}
            value={unverified.length}
            tone={unverified.length > 0 ? "warn" : "neutral"}
            detail={t("kpi.unverifiedDetail")}
            icon="fas fa-user-times"
          />
        </Col>
      </Row>

      <Nav tabs className="mb-3">
        {tabs.map(({ key, label, count, icon }) => (
          <NavItem key={key}>
            <NavLink
              className={classnames({ active: segment === key })}
              onClick={() => setSegment(key)}
              style={{ cursor: "pointer" }}
            >
              <i className={`${icon} mr-2`} />
              {label} <span className="text-muted">({count})</span>
            </NavLink>
          </NavItem>
        ))}
      </Nav>

      <Row style={{ rowGap: 16 }}>
        <Col xs={12} xl={8}>
          <UsersListCard
            totalUsers={current.users.length}
            users={pageUsers}
            onUserClick={(user) => navigate(`/users/${user.id}`)}
            isLoading={current.isLoading}
            pagination={paginationProps.pagination}
            goToPage={paginationProps.goToPage}
            changePageSize={paginationProps.changePageSize}
            filters={current.props.filters}
            changeFilters={current.props.changeFilters}
            zeroFeeTierIds={zeroPriceTierIds}
            sort={userSort}
            changeSort={(field, direction) => setUserSort(direction ? { [field]: direction } : {})}
            columnsToHide={current.hide}
            title={tabs.find((x) => x.key === segment)?.label}
          />
        </Col>
        <Col xs={12} xl={4}>
          <Card body className="p-0">
            <div className="px-3 pt-3" style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>
              {t("map", { segment: tabs.find((x) => x.key === segment)?.label })}
            </div>
            <MapGraph
              data={Object.entries(perCountry).map(([key, values]): MapDataItem => ({
                id: key,
                value: segment === "nonClients" ? values.nonClients : values.clients,
              }))}
              isLoading={loadingMap}
              className=""
              style={{}}
            />
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Users;
